import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { newUi } from "../lib/featureFlags";
import { LOG_STEP_PARAM, RESTORABLE_LOG_STEPS, logStepFromParams } from "../lib/logFlowStep";
import { GROUND_LAT_PARAM, GROUND_LNG_PARAM, GROUND_NAME_PARAM, GROUND_PARAM, wantsGroundHint } from "../lib/activityCreate";

/**
 * Issue #904 — the `logStep` half of `PerformanceTracking.jsx`'s new
 * log-a-match flow, pulled out into its own hook for the same reason
 * `hooks/useGroundsNearYou.js`'s own header gives for its extraction: this
 * repo measures `max-lines-per-function` against `PerformanceTracking`
 * itself (`eslint.config.mjs`'s own budget header cites it by name), and a
 * plain `useState("ground")` growing two URL-sync effects in place would
 * have pushed the function straight past the 710-line budget.
 *
 * See `lib/logFlowStep.js`'s own header for the full derivation of WHY a
 * refresh needs this at all — the short version: `open` (from
 * `useCreateDialog`) only reopens from a `?new=1` that is consumed and
 * stripped from the URL on the very first mount, and `logStep` had no URL
 * tie at all, so a refresh past step 1 used to drop the player back on the
 * shipped list with the flow simply gone.
 *
 * `open`/`setOpen` are passed in rather than owned here — they come from
 * `useCreateDialog`, a hook several OTHER pages share unchanged, and this
 * one only ever needs to read/flip that page's own copy, never mint its
 * own separate dialog-visibility state. `isEditing`, added in review round
 * 3 (F8), is `PerformanceTracking.jsx`'s own `Boolean(editingId)`.
 *
 * REVIEW ROUND 3 — three findings, all in the URL-sync effect below:
 *
 * F6 — a second refresh lost `?step=`. On a mount that reopens from a
 * restorable `?step=` with no `?new=1` (already consumed on an earlier
 * mount), `open` starts `false` and only becomes `true` a moment later,
 * once the reopen effect below has asked for it — a `setOpen` call does
 * not retroactively change the closure an ALREADY-RUNNING effect from the
 * SAME commit sees. The sync effect used to run right after the reopen
 * effect, in that same commit, and read that still-stale `open=false` —
 * which made it strip the very `?step=` this mount had just read and was
 * about to restore. It came back a beat later (the next commit, once
 * `open` genuinely was `true`), so a single refresh still looked fine —
 * but that was a real strip-then-restore round trip through history via
 * `replace: true` twice, and a second refresh landing inside that window
 * had nothing left to read. `reopeningRef` folds the still-reopening case
 * into `effectivelyOpen`, so this effect's very first run already sees
 * the truth instead of a transient lie; it self-clears the moment `open`
 * genuinely reads `true`, so it never outlives the mount it was minted
 * for (in particular: closing the flow afterwards behaves exactly as
 * before, not "stuck open").
 *
 * F7 — a refresh on the add-ground panel (`logStep === "add-ground"`)
 * used to fall back to the SHIPPED LIST, not even the flow's own start:
 * `desired` was `null` for any non-restorable step, which cleared
 * `?step=` outright while add-ground was showing, so a refresh there had
 * neither `?step=` nor `?new=1` (already consumed) to reopen from.
 * `desired` now falls back to `"ground"` for a non-restorable, non-editing
 * step, so a refresh mid-add-ground lands on step 1 instead — the issue's
 * own stated floor ("at least the new flow's start").
 *
 * F8 — a refresh mid-EDIT must never turn into a blank new match. An edit
 * session has no id of its own in the URL (`editingId` is page state,
 * gone on refresh), so writing `?step=form` while editing — the same
 * value a fresh create's own step 2 uses — meant a refresh reopened step
 * 2 with nothing to edit: a blank form masquerading as the edit that was
 * in progress. `isEditing` forces `desired` to `null` outright: no write,
 * and it also CLEARS any `?step=` a prior, non-edit visit left behind,
 * because entering edit mode itself changes `logStep` to `"form"`
 * (`openEdit`), which re-runs this effect with `isEditing` now `true`.
 *
 * REVIEW ROUND 3, FINAL NARROW ROUND (A3) — F13 (regression): "Skip it" on
 * the saved step (`handleSkipToDashboard`) calls `closeDialog(false)` —
 * `open`/`logStep` changing to `false`/`"ground"` — and `navigate
 * ("/dashboard")` together, synchronously, in the same handler. That
 * state change is exactly what re-runs the sync effect below, and it used
 * to write regardless of where the SPA had actually navigated to by the
 * time it ran: `setSearchParams(..., { replace: true })` replaces the
 * CURRENT top-of-history entry using the pathname this hook's own
 * `useSearchParams()` was called against — if that fires once the router
 * has already moved to `/dashboard`, `replace` overwrites THAT entry with
 * a reconstructed `/performance` URL, which is indistinguishable from
 * "Skip" bouncing straight back to the page it just left (watched failing
 * live: journeys 1 and 33 both timed out waiting for the URL to reach
 * /dashboard).
 * `window.location.pathname` is checked before every write: once it is no
 * longer `/performance`, this effect never touches the URL again, no
 * matter which commit it runs in.
 *
 * PR #921 REVIEW (F3) — `?ground=`/`?glat=`/`?glng=`/`?gname=` (issue
 * #766's "Log a match here", `GroundCard.jsx`) are now read and spent
 * HERE, not in a sibling effect inside `LogMatchGroundStep.jsx`. That
 * sibling effect used to call its OWN `useSearchParams()` and strip the
 * params itself, and the strip did not stick: `useCreateDialog`'s own
 * strip of `?new=1` and this hook's own `?step=` sync effect each build
 * their `next` `URLSearchParams` from THEIR OWN closed-over `sp` — a
 * snapshot from the render that mounted all three effects — so whichever
 * of the three writes LAST reconstructs a URL that still carries whatever
 * the OTHER two had already tried to remove. One reader, one writer fixes
 * it: `presetGround` is read once, on the same lazy-initializer shape
 * `logStep` already uses, and its removal rides the SAME `setSp` call the
 * step-sync effect below already makes — never a second, competing
 * write. `LogMatchGroundStep.jsx` receives the resolved value as a PROP
 * now, and reads no search params of its own.
 *
 * SPENT THE MOMENT THE FLOW LEAVES THE GROUND STEP — including a LATER
 * remount. Pressing "Change" from step 2 sends `logStep` back to
 * `"ground"`, which remounts `LogMatchGroundStep`; if `presetGround`
 * were still sitting in this hook's state, that remount would re-run the
 * exact same auto-pick and silently defeat "Change". The effect below
 * clears it the first time `logStep` moves OFF `"ground"` (i.e. the
 * moment a ground — preset or chosen — was actually picked), so a later
 * return to the ground step starts genuinely blank.
 */
export function useLogFlowStep(open, setOpen, isEditing = false) {
  const [sp, setSp] = useSearchParams();
  // Restored from `?step=` on mount (flag on only — the shipped, flag-off
  // dialog carries no such param and stays exactly the single
  // `useState("ground")` it always was before this issue).
  const [logStep, setLogStep] = useState(() => (newUi("log-ground") && logStepFromParams(sp)) || "ground");
  const [presetGround, setPresetGround] = useState(() => wantsGroundHint(sp));
  useEffect(() => {
    if (logStep !== "ground" && presetGround.groundId) setPresetGround({ groundId: null, name: null, latitude: null, longitude: null });
  }, [logStep, presetGround]);

  // F6 — frozen at what THIS mount actually saw: was a restorable
  // `?step=` asking to reopen the flow without `?new=1`? Read once (a
  // `useRef` initial value is used only on the very first render), then
  // self-cleared below the first time `open` genuinely reads `true`.
  const reopeningRef = useRef(newUi("log-ground") && Boolean(logStepFromParams(sp)) && !open);

  // A refresh mid-flow strips `?new=1` before `open` is read (already
  // consumed on THIS mount too, same as every mount), but a restorable
  // `?step=` proves the flow was genuinely mid-way through, so it reopens
  // the dialog even without the create param.
  useEffect(() => {
    if (reopeningRef.current) setOpen(true);
    // Mount-only: a one-time "was I deep-linked here" check, not a rule
    // that should re-fire on every `sp`/`open` change (that would fight
    // `closeDialog`, which sets `open` back to `false` on purpose).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps `?step=` in sync with `logStep` as the player moves through the
  // flow, so the NEXT refresh restores the step they are actually on.
  useEffect(() => {
    if (!newUi("log-ground")) return;
    // F13 — never write once the SPA has navigated away from /performance
    // (see this file's own header). `window.location.pathname`, read
    // fresh right here — not React Router's own `useLocation()` value
    // (tried first; still stale on the very commit this needs it not to
    // be, because a `history.pushState` from `navigate(...)` lands on
    // `window.location` synchronously, inside the SAME event handler that
    // changed `open`/`logStep`, while `useLocation()`'s reactive value
    // only catches up on ITS OWN next render — one commit later than the
    // effect below, which fires off the `open`/`logStep` change alone).
    // Checked FIRST, before anything else below computes a `desired`
    // value that would otherwise still try to write it.
    if (window.location.pathname !== "/performance") return;
    // F6 — see this file's own header. `open` may still read `false` on
    // the commit that JUST asked to flip it true; `reopeningRef` covers
    // that one transient beat, then retires itself for good.
    const effectivelyOpen = open || reopeningRef.current;
    if (open) reopeningRef.current = false;
    // F7/F8 — see this file's own header.
    const desired = isEditing
      ? null
      : effectivelyOpen
        ? (RESTORABLE_LOG_STEPS.includes(logStep) ? logStep : "ground")
        : null;
    // F3 — `ground` rides this SAME write, whenever there is one to make:
    // the guard below now also fires while `ground` is still on the URL,
    // even if `step` itself already reads as `desired`, so the one param
    // this hook never otherwise touches still gets carried to zero rather
    // than left for a write that never comes.
    const groundParamsPresent = [GROUND_PARAM, GROUND_LAT_PARAM, GROUND_LNG_PARAM, GROUND_NAME_PARAM].some((p) => sp.has(p));
    if (sp.get(LOG_STEP_PARAM) === desired && !groundParamsPresent) return;
    const next = new URLSearchParams(sp);
    if (desired) next.set(LOG_STEP_PARAM, desired);
    else next.delete(LOG_STEP_PARAM);
    for (const p of [GROUND_PARAM, GROUND_LAT_PARAM, GROUND_LNG_PARAM, GROUND_NAME_PARAM]) next.delete(p);
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, logStep, isEditing]);

  return [logStep, setLogStep, presetGround];
}

export default useLogFlowStep;
